/**
 * <cart-upsells> — "Upgrade Your Setup" cart drawer upsells.
 *
 * Defined here (a globally-loaded asset) rather than inline in the snippet, so the custom
 * element is always registered on page load. The cart drawer re-renders via innerHTML, and
 * inline <script> tags injected that way don't execute — so an inline definition only ran
 * after a full refresh, which is why clicks did nothing on the first AJAX-rendered drawer.
 *
 * Behaviour: the whole card is the add-to-cart target; clicking a colour swatch only
 * switches the selected variant.
 */
if (!customElements.get('cart-upsells')) {
  class CartUpsells extends HTMLElement {
    connectedCallback() {
      this._onClick = this._onClick.bind(this);
      this.addEventListener('click', this._onClick);

      this.querySelectorAll('[data-upsell-card]').forEach((card) => {
        const active =
          card.querySelector('[data-upsell-swatch][aria-pressed="true"]') ||
          card.querySelector('[data-upsell-swatch]');
        if (active) this.selectVariant(card, active);
      });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    parse(html) {
      return new DOMParser().parseFromString(html, 'text/html');
    }

    _onClick(event) {
      // Swatch click only switches the selected colour — it must NOT add to cart.
      const swatch = event.target.closest('[data-upsell-swatch]');
      if (swatch && this.contains(swatch)) {
        if (swatch.disabled) return;
        const card = swatch.closest('[data-upsell-card]');
        if (card) this.selectVariant(card, swatch);
        return;
      }

      // Anywhere else on the card adds to cart (whole card is the add target).
      const card = event.target.closest('[data-upsell-card]');
      if (card && this.contains(card)) {
        const addBtn = card.querySelector('[data-upsell-add]');
        if (addBtn && addBtn.disabled) return; // in cart / sold out / blocked
        event.preventDefault();
        this.addUpsell(card);
      }
    }

    selectVariant(card, swatch) {
      const { variantId, price, available, inCart } = swatch.dataset;
      card.dataset.variantId = variantId;

      // selected visual is pure CSS, keyed on aria-pressed.
      // card image intentionally stays the main product image.
      card.querySelectorAll('[data-upsell-swatch]').forEach((s) => {
        s.setAttribute('aria-pressed', s === swatch ? 'true' : 'false');
      });

      // Optional — only present if the card renders a standalone price element.
      const priceEl = card.querySelector('[data-upsell-price]');
      if (priceEl && price != null) priceEl.textContent = price;

      const addBtn = card.querySelector('[data-upsell-add]');
      const label = card.querySelector('[data-upsell-label]');
      const icon = card.querySelector('[data-upsell-icon]');
      if (addBtn) {
        addBtn.dataset.variantId = variantId;
        const isAvailable = available !== 'false';
        const isInCart = inCart === 'true';
        const addable = isAvailable && !isInCart;
        addBtn.disabled = !addable;
        if (label)
          label.textContent = isInCart ? 'In cart' : isAvailable ? 'Add' : 'Sold out';
        if (icon) {
          icon.style.display = addable ? '' : 'none';
          icon.style.transform = '';
          if (addable) {
            icon.innerHTML =
              '<path d="M6 2.5v7M2.5 6h7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>';
          }
        }
      }
    }

    updateDrawer(state) {
      const drawer = document.querySelector('cart-drawer');

      if (typeof drawer?.renderContents === 'function') {
        drawer.renderContents(state);
        return;
      }

      const { sections } = state;
      if (!sections) return;

      if (sections['cart-drawer']) {
        const fresh = this.parse(sections['cart-drawer']).querySelector('#CartDrawer');
        const live = document.querySelector('#CartDrawer');
        if (fresh && live) live.innerHTML = fresh.innerHTML;
      }
      if (sections['cart-icon-bubble']) {
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble) bubble.innerHTML = this.parse(sections['cart-icon-bubble']).body.innerHTML;
      }

      drawer?.open?.();
    }

    playAddedAnimation(card, label, icon, doneText = 'Added') {
      return new Promise((resolve) => {
        if (label) label.textContent = doneText;
        if (icon) {
          icon.style.display = '';
          icon.style.transform = 'none';
          // icon.innerHTML =
            // '<path d="M2.5 6.4l2.4 2.4 4.6-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>';
        }

        if (this.prefersReducedMotion()) {
          resolve();
          return;
        }

        card.style.transition = 'transform 180ms ease, border-color 200ms ease';
        card.style.borderColor = 'var(--color-Text, #1a1a1a)';
        card.style.transform = 'scale(1.04)';
        setTimeout(() => {
          card.style.transform = 'scale(1)';
        }, 180);

        setTimeout(resolve, 480);
      });
    }

    highlightNewItem(variantId) {
      requestAnimationFrame(() => {
        const row = document.querySelector(`#CartDrawer [data-variant-id="${variantId}"]`);
        if (!row) return;

        row.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
        if (this.prefersReducedMotion()) return;

        row.style.transition = 'none';
        row.style.opacity = '0';
        row.style.transform = 'translateY(-10px)';
        row.style.backgroundColor = 'rgba(0,0,0,0.04)';
        row.getBoundingClientRect();

        requestAnimationFrame(() => {
          row.style.transition =
            'opacity 320ms ease, transform 320ms cubic-bezier(0.22, 1, 0.36, 1), background-color 700ms ease';
          row.style.opacity = '1';
          row.style.transform = 'translateY(0)';
          row.style.backgroundColor = 'transparent';
        });

        setTimeout(() => {
          row.style.transition = '';
          row.style.opacity = '';
          row.style.transform = '';
          row.style.backgroundColor = '';
        }, 750);
      });
    }

    async addUpsell(card) {
      const variantId = card.dataset.variantId;
      if (!variantId || card.getAttribute('aria-busy') === 'true') return;

      // Warranty upgrade: data-upsell-replace names a variant to remove right
      // after this one is added (2-year → 3-year swap, cart-upsells.liquid).
      const replaceId = card.dataset.upsellReplace || null;

      const label = card.querySelector('[data-upsell-label]');
      const icon = card.querySelector('[data-upsell-icon]');
      const addBtn = card.querySelector('[data-upsell-add]');
      const original = label ? label.textContent : '';

      card.setAttribute('aria-busy', 'true');
      if (label) label.textContent = replaceId ? 'Upgrading…' : 'Adding…';

      try {
        const addBody = {
          // `_upsell` (hidden — underscore prefix) tags the line so the cart drawer can keep
          // upsell-added items rendered LAST (via flex `order`), regardless of where Shopify
          // places the new line in cart.items. Persists across re-renders since it's a real
          // line-item property.
          items: [{ id: Number(variantId), quantity: 1, properties: { _upsell: 'true' } }],
        };
        // On an upgrade the drawer is rendered from the follow-up removal
        // response instead, so skip the section render here.
        if (!replaceId) {
          addBody.sections = ['cart-drawer', 'cart-icon-bubble'];
          addBody.sections_url = window.location.pathname;
        }

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(addBody),
        });

        let state = await response.json();
        if (!response.ok) throw new Error(state.description ?? 'Add to cart failed');

        if (replaceId) {
          // Added first, removed second — the cart never flashes empty of a warranty.
          const swap = await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              updates: { [replaceId]: 0 },
              sections: ['cart-drawer', 'cart-icon-bubble'],
              sections_url: window.location.pathname,
            }),
          });
          const swapState = await swap.json();
          if (!swap.ok) throw new Error(swapState.description ?? 'Warranty upgrade failed');
          state = swapState;
        }

        await this.playAddedAnimation(card, label, icon, replaceId ? 'Upgraded' : 'Added');

        const sw = card.querySelector(`[data-upsell-swatch][data-variant-id="${variantId}"]`);
        if (sw) {
          sw.dataset.inCart = 'true';
        } else if (addBtn) {
          addBtn.disabled = true;
        }

        this.updateDrawer(state);
        this.highlightNewItem(variantId);

        if (sw && card.isConnected) this.selectVariant(card, sw);

        if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-upsells',
            productVariantId: variantId,
            cartData: state,
          });
        }
      } catch (error) {
        console.error('[cart-upsells]', error);
        if (label) label.textContent = 'Try again';
        card.style.transform = 'none';
        card.style.borderColor = '';
        setTimeout(() => {
          if (label) label.textContent = original;
          card.removeAttribute('aria-busy');
        }, 1500);
      } finally {
        card.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('cart-upsells', CartUpsells);
}
